import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { ControllStatus, ProjectedScenario } from 'src/api/types';
import { DataTitle, Poppins, Spacer, Tooltip } from 'src/common';
import Button from 'src/components/form/Button';
import styled from 'styled-components';
import { Modifier } from 'src/components/Modifier';
import _ from 'lodash';
import { checkValidity, recalc, convertToModifiersGql } from 'src/components/ProjectedRisk/util';
import { calcModChart, CalcModChartParameters } from 'src/api/scenario';
import { ControlStateItem, ModifierStateItem } from 'src/components/ProjectedRisk/types';
import { getPercentage, waitFor } from 'src/utils/misc';
import GenericTable from 'src/components/GenericTable';
import { useColumnsControls } from './useColumnsControls';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import useVersion from 'src/hooks/useVersion';
import { TableIds } from 'src/api/types/misc';
import { useCache } from 'src/graphql';
import { ScenarioQuery, ScenarioRmpQuery } from 'src/graphql/_generated';

const Div = styled.div`
  .tf-head {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    grid-gap: 10px;
    gap: 10px;
  }

  .switch-container {
    display: flex;
    justify-content: center;
  }
`;

interface TableFormProps {
  scenario: NonNullable<ScenarioQuery['scenario']>;
  riskManagementPlans: NonNullable<ScenarioRmpQuery['scenario']>['riskManagementPlans'];
  setCompleteChart: (s: { x: number; y: number }[]) => void;
  setLiveChart: (s: { x: number; y: number }[]) => void;
  isInModal?: boolean;
}

export const TableForm: React.FC<TableFormProps> = ({
  scenario,
  setCompleteChart,
  setLiveChart,
  riskManagementPlans,
  isInModal,
}) => {
  const [modifiers, setModifiers] = useState<ModifierStateItem[]>([]);
  const [controls, setControls] = useState<ControlStateItem[]>([]);
  const [submitHandlers, setSubmitHandlers] = useState<(() => void)[]>([]);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const { versionId } = useVersion();
  const { refetchQuery } = useCache();

  const isStateDirty = !_.isEqual(
    modifiers.map((el) => el.modifiers),
    riskManagementPlans.map((rmp) => rmp.modifiers),
  );

  const original: ProjectedScenario = useMemo(
    () => ({
      id: scenario.id,
      upper: scenario.baseRisk.upper || 0,
      lower: scenario.baseRisk.lower || 0,
      frequency_times: getPercentage(scenario.baseRisk.frequencyTimes, scenario.baseRisk.frequencyYears),
      frequency_years: scenario.baseRisk.frequencyYears || 0,
    }),
    [scenario],
  );

  const [callCalculate, { isLoading }] = useMutation(calcModChart);

  useEffect(() => {
    if (riskManagementPlans.length === 0) return;

    const modifiers = _.chain(riskManagementPlans)
      .map((rmp) => ({
        id: rmp.control?.id || '',
        modType: rmp.control?.strategy?.toLowerCase() || '',
        modifiers: convertToModifiersGql(rmp.modifiers || []),
        isValid: true,
      }))
      .filter((el) => ['treat', 'transfer'].includes(el.modType))
      .value();

    setModifiers(modifiers);

    const sortedControls = _.chain(riskManagementPlans)
      .map((rmp) => {
        const mod = _.find(modifiers, { id: rmp.control?.id })?.modifiers;
        const strategy = rmp.control?.strategy?.toLowerCase() || '';
        const isTolerateTerminate = ['tolerate', 'terminate'].includes(strategy);
        const isDisabled = isInModal && isTolerateTerminate;

        return {
          control: {
            ...rmp.control,
            id: rmp.control?.id || '',
            name: rmp.control?.name || '',
            modifiers: mod,
          },
          isEnabled: !isDisabled,
          projectedScenario: original,
          sortPriority: isDisabled ? 1 : 0,
        };
      })
      .orderBy(['sortPriority', 'id'], ['asc', 'asc'])
      .value();

    // @ts-ignore
    setControls(sortedControls);
  }, [riskManagementPlans, isInModal, original]);

  useEffect(() => {
    recalc({
      controls,
      original,
      modifiers,
      setControls,
    });
  }, [modifiers, controls, original]);

  const toggleControl = async (isEnabled: boolean, id: string) => {
    setControls((s) =>
      s.map((control) => {
        if (control.control.id === id) {
          return { ...control, isEnabled };
        }
        return control;
      }),
    );
    await waitFor(0);
    recalc({ controls, original, modifiers, setControls });
  };

  const addSubmitHandler = useCallback((handler: () => void) => {
    setSubmitHandlers((prevHandlers) => [...prevHandlers, handler]);
  }, []);

  const handleSaveAll = async () => {
    setIsSavingAll(true);
    submitHandlers.forEach((handler) => handler());
    await waitFor(submitHandlers.length * 500);
    await refetchQuery({ include: ['scenario', 'scenarioControls'] });

    setIsSavingAll(false);
  };

  const handleCalculate = () => {
    const filteredModifiers = modifiers.filter(
      (m) =>
        controls.find((el) => el.control.id === m.id)?.isEnabled &&
        controls.find((el) => el.control.id === m.id)?.control.status !== ControllStatus.Abandoned &&
        controls.find((el) => el.control.id === m.id)?.control.status !== ControllStatus.Retired,
    );

    const params: CalcModChartParameters = {
      scenario_id: scenario.id,
      modifiers: {
        treat: _.chain(filteredModifiers)
          .flatMap((el) => {
            const controlIsLive = controls.find((c) => c.control.id === el.id)?.control.status === ControllStatus.Live;
            return _.get(el, 'modifiers.treat', []).map((treatMod) => ({
              ...treatMod,
              isLive: controlIsLive,
            }));
          })
          .value(),
        transfer: _.chain(filteredModifiers)
          .flatMap((el) => {
            const controlIsLive = controls.find((c) => c.control.id === el.id)?.control.status === ControllStatus.Live;
            return _.get(el, 'modifiers.transfer', []).map((transMod) => ({
              ...transMod,
              isLive: controlIsLive,
            }));
          })
          .value(),
        notes: [],
      },
    };

    callCalculate(params).then((res) => {
      if (res && res.data.completeChart && res.data.liveChart) {
        setCompleteChart(res.data.completeChart.chart);
        setLiveChart(res.data.liveChart.chart);
      }
    });
  };

  const allModifiersValid = _.every(modifiers, (el) => el.isValid !== false);
  const { columns, GTColumnSelectAnchorExported } = useColumnsControls({
    data: controls,
    scenarioId: scenario.id,
    assessmentId: scenario.assessment.id,
    toggleControl,
  });

  return (
    <Div>
      <DataTitle $noMargin>CONTROLS</DataTitle>
      <Poppins px={14}>
        You can adjust which controls are applied to the risk scenario. If controls have risk modifiers set, this will
        adjust the loss exceedence curves accordingly. Modifiers may be adjusted on this screen to simulate possible
        future risk states. After updating controls, use the “Recalculate” button to run the simulation. To save any
        modifier changes to the scenario, use the “Save Updated Modifiers” button.
      </Poppins>
      {!versionId && (
        <>
          <Spacer $px={10} />
          <div className="tf-head">
            <Button
              secondary
              onClick={() => {
                handleSaveAll();
                mpEvent(MPEvents.ButtonClick, {
                  button: 'Save updated modifiers',
                  modal: 'Projected risk:Scenario modal',
                  tags: ['SCENARIO', 'MODIFIERS'],
                });
              }}
              disabled={!isStateDirty || !allModifiersValid || isSavingAll}
              loading={isSavingAll}
            >
              SAVE UPDATED MODIFIERS
            </Button>
            <Tooltip
              dataHtml="Get an updated view of the risks without saving them"
              dataId="recalculate"
              place="bottom"
            >
              <Button
                primary
                onClick={() => {
                  handleCalculate();
                  mpEvent(MPEvents.ButtonClick, {
                    button: 'Recalculate',
                    modal: 'Projected risk:Scenario modal',
                    tags: ['SCENARIO', 'MODIFIERS'],
                  });
                }}
                disabled={!allModifiersValid || isLoading}
                loading={isLoading}
              >
                RECALCULATE
              </Button>
            </Tooltip>
          </div>
        </>
      )}
      <Spacer $px={20} />
      <GenericTable
        tableId={TableIds.prScenarioScenarios}
        columns={columns}
        data={controls}
        GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
        searchable={['strategy', 'name', 'owner', 'frameworkLibrary_shortname', 'controlId', 'status', 'updated_at']}
        initialSort={[{ id: 'expected_active_date', desc: false }]}
        rowDisabled={(el) => {
          return el.control.strategy !== 'Treat' && el.control.strategy !== 'Transfer';
        }}
        expandContent={({ control: cont, projectedScenario }) => {
          if (cont.strategy === 'Treat' || (cont.strategy === 'Transfer' && projectedScenario)) {
            const mod = modifiers?.find((el) => el.id === cont.id)?.modifiers;

            const rmp = riskManagementPlans.find((rmp) => rmp.control?.id === cont.id);
            const original = rmp?.modifiers;
            const isDirtyOutside = !_.isEqual(mod, original);

            return (
              <>
                {projectedScenario && (
                  <Modifier
                    projectedScenario={{
                      ...projectedScenario,
                      frequency_times: getPercentage(
                        projectedScenario.frequency_times,
                        projectedScenario.frequency_years,
                      ),
                    }}
                    startingValues={{
                      frequency_times: getPercentage(
                        projectedScenario.frequency_times,
                        projectedScenario.frequency_years,
                      ),
                      lower: projectedScenario.lower,
                      upper: projectedScenario.upper,
                    }}
                    control={{ ...cont, modifiers: mod }}
                    useCallback={async (s) => {
                      const updatedModifiers = modifiers?.map((el) => {
                        if (el.id === cont.id) {
                          return {
                            ...el,
                            modifiers: s.values,
                            isValid:
                              s.isValid &&
                              checkValidity({
                                mods: mod,
                                strategy: cont.strategy?.toLowerCase() || '',
                                projectedScenario: { ...projectedScenario },
                              }),
                          };
                        }
                        return el;
                      });

                      if (_.isEqual(modifiers, updatedModifiers)) return;

                      setModifiers(updatedModifiers);
                    }}
                    addSubmitHandler={addSubmitHandler}
                    isDirtyOutside={isDirtyOutside}
                    disabled={!!versionId}
                    cacheInvalidation={['control', 'assessment', 'scenario']}
                    withCopy
                  />
                )}
              </>
            );
          }
          return null;
        }}
      />
    </Div>
  );
};
